import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { convertToHuman, formattingResponseTime } from "../../../utils/time.utils";
import SendMessageForm from "../SendMessageForm/SendMessageForm";
import clsx from "clsx";
import MessageContent from "../MessageContent/MessageContent";
import EventEmitter from "../../../utils/EventEmitter";
import SocketContext from "../../../providers/Socket";
import { getOtherUserId, getUserInfo, isSender as checkIsSender, isSeller as checkIsSeller } from "../../../utils/chat.utils";
import { getDataFromStorage, isDeviceMobile } from "../../../libs/auth.helper";
import useConfig from "../../../hooks/use-config";
import useTranslation from "../../../hooks/use-translation";
import loggingCallPostApi from "../../../api/logging-call-post.api";
import { FaCheck, FaCheckDouble, FaEllipsisV, FaTimes } from "react-icons/fa";
import ChatSettings from "../ChatSettings/ChatSettings";
import NotConnectedInternet from "../NotConnectedInternet/NotConnectedInternet";
import GeneralTips from "../GeneralTips/GeneralTips";
import useInfiniteScroll from "react-infinite-scroll-hook";
import NotConnected from "../NotConnected/NotConnected";
import { extractUtms, logSendMessageClick, logViewListingClick } from "../../../logging/helpers/commonLoggingFunctions";
import { SystemMessageButton } from "../MessageContent/Types/SystemMessageButton/SystemMessageButton";
import ResponseTime from "../ResponseTime/ResponseTime";
import Spearator from "../Seperator/Spearator";
import { ERROR, OFFLINE_MESSAGES, generalTips, jobTips } from "./ChatMessages";
import { EnumElementName } from "../../../logging/types/LoggingTypes";
import getUnmaskedMobileNumberApi from "../../../api/get-unmasked-mobile-number.api";
import getPostViewNodeApi from "../../../api/get-post-view-node.api";
const MESSAGE_STATUSES = {
    SENT: 0,
    DELIVERED: 1,
    SEEN: 2
};
const DEFAULT_CHAT_INFO = {
    responseDuration: "",
    hasMore: true,
    tips: []
};
const LIMIT = 20;
const Chat = (props) => {
    var _a;
    const isDrawer = true;
    const { chat, swapChatRooms } = props;
    const messageRef = useRef(null);
    const { socket, loadChats, isConnectionFailed } = useContext(SocketContext);
    const [chatInfo, setChatInfo] = useState(DEFAULT_CHAT_INFO);
    const [isTyping, setIsTyping] = useState(false);
    const [messages, setMessages] = useState([]);
    const [offlineMessages, setOfflineMessages] = useState([]);
    const config = useConfig();
    const { translate, direction } = useTranslation();
    const callRef = useRef(null);
    const [isBlocked, setIsBlocked] = useState(false);
    const [isFetchingDisabled, setIsFetchingDisabled] = useState(false);
    const [chatId, setChatId] = useState((_a = chat.details) === null || _a === void 0 ? void 0 : _a.rooms[0].uid);
    const [isPhoneHidden, setIsPhoneHidden] = useState(true);
    const [post, setPost] = useState(undefined);
    const [revealKey, setRevealKey] = useState(null);
    const updateChatInfoHandler = (args) => {
        setChatInfo((prevChatInfo) => {
            return Object.assign(Object.assign({}, prevChatInfo), args);
        });
    };
    const loadMessagesHandler = useCallback((params) => {
        return new Promise((resolve, reject) => {
            var _a, _b, _c, _d, _e, _f, _g;
            if (!socket)
                return;
            params = Object.assign(Object.assign({}, params), { limit: LIMIT });
            const chatDetail = [(_b = (_a = chat.details) === null || _a === void 0 ? void 0 : _a.pid) === null || _b === void 0 ? void 0 : _b.toString(), (_d = (_c = chat.details) === null || _c === void 0 ? void 0 : _c.oid) === null || _d === void 0 ? void 0 : _d.toString(), (_g = (_f = (_e = chat.details) === null || _e === void 0 ? void 0 : _e.rooms[0]) === null || _f === void 0 ? void 0 : _f.uid) === null || _g === void 0 ? void 0 : _g.toString()];
            socket === null || socket === void 0 ? void 0 : socket.emit('history-messages', ...chatDetail, params, function (newMessages) {
                if (newMessages.length < LIMIT) {
                    setChatInfo((old) => { return Object.assign(Object.assign({}, old), { hasMore: false }); });
                }
                if (!newMessages.length) {
                    resolve([]);
                    return;
                }
                newMessages.forEach((message) => {
                    if (message.status != 2 && !checkIsSender(message)) {
                        socket === null || socket === void 0 ? void 0 : socket.emit('seen', ...chatDetail, message.mid.toString());
                    }
                });
                resolve(newMessages);
            });
        });
    }, [socket, messages, chat]);
    const scrollEventHandler = useCallback(() => {
        setIsFetchingDisabled(true);
        setTimeout(() => {
            loadMessagesHandler({ before_mid: messages[messages.length - 1].mid }).then(newMessages => {
                if (!newMessages.length) {
                    updateChatInfoHandler({ hasMore: false });
                    return;
                }
                setMessages((prevMessages) => {
                    return prevMessages.concat(newMessages);
                });
                setIsFetchingDisabled(false);
            });
        }, 250);
    }, [messages]);
    const [infiniteScrollRef, { rootRef }] = useInfiniteScroll({
        loading: isFetchingDisabled,
        hasNextPage: chatInfo.hasMore && !isFetchingDisabled,
        onLoadMore: scrollEventHandler,
    });
    const seenMessageHandler = useCallback((pid, oid, uid, mid) => {
        setMessages((prevMessages) => {
            let clonedMessages = [...prevMessages];
            const foundIndex = clonedMessages.findIndex(it => it.mid == mid);
            clonedMessages[foundIndex] = Object.assign(Object.assign({}, prevMessages[foundIndex]), { status: MESSAGE_STATUSES.DELIVERED });
            return [
                ...clonedMessages.slice()
            ];
        });
    }, [setMessages]);
    const deliveredMessageHandler = useCallback((pid, oid, uid, mid) => {
        setMessages((prevMessages) => {
            let clonedMessages = [...prevMessages];
            const foundIndex = clonedMessages.findIndex(it => it.mid == mid);
            clonedMessages[foundIndex] = Object.assign(Object.assign({}, prevMessages[foundIndex]), { status: MESSAGE_STATUSES.SENT });
            return [
                ...clonedMessages.slice()
            ];
        });
    }, [setMessages]);
    const scrollToBottom = () => {
        if (scrollableRootRef !== null && scrollableRootRef.current !== null) {
            const root = scrollableRootRef.current;
            root.scrollTop = root.scrollHeight - root.clientHeight;
        }
    };
    const saveOfflineMessageHandler = useCallback((message) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        let data = Object.assign({ oid: (_a = chat.details) === null || _a === void 0 ? void 0 : _a.oid, pid: (_b = chat.details) === null || _b === void 0 ? void 0 : _b.pid, uid: (_c = chat.details) === null || _c === void 0 ? void 0 : _c.rooms[0].uid, post_img: (_d = chat.details) === null || _d === void 0 ? void 0 : _d.post_img, post_title: (_e = chat.details) === null || _e === void 0 ? void 0 : _e.post_title, post_country: (_f = chat.details) === null || _f === void 0 ? void 0 : _f.post_country, post_type: "default", sender_id: getDataFromStorage().userInfo.id, recipient_id: getOtherUserId(chat.details), recipient_name: (_g = chat.details) === null || _g === void 0 ? void 0 : _g.rooms[0].name, recipient_avatar: (_h = chat.details) === null || _h === void 0 ? void 0 : _h.rooms[0].avatar, sender_avatar: (_j = getUserInfo()) === null || _j === void 0 ? void 0 : _j.profile_picture, sender_name: (_k = getUserInfo()) === null || _k === void 0 ? void 0 : _k.full_name, status: -1 }, message);
        const messageObject = Object.assign({ sent_at: Date.now(), mid: Math.random(), post_shop_avatar: '', post_shop_name: '', room: '', send_date: '', sender_lang: '', sender_platform: '', sender_user_agent: '', timestamp: 0 }, data);
        setOfflineMessages((prev => [messageObject, ...prev]));
        setTimeout(() => {
            props.onLoadChats();
            scrollToBottom();
        }, 10);
    }, [isConnectionFailed]);
    const sendMessageHandler = useCallback((messageContent) => {
        return new Promise((resolve, reject) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
            /*----------------
            *    LOGGING
            *---------------*/
            if (chat && chat.details && chat.details.pid)
                logSendMessageClick(chat.details.pid, chat.details.oid);
            let data = Object.assign(Object.assign({ oid: (_a = chat.details) === null || _a === void 0 ? void 0 : _a.oid, pid: (_b = chat.details) === null || _b === void 0 ? void 0 : _b.pid, uid: (_c = chat.details) === null || _c === void 0 ? void 0 : _c.rooms[0].uid, post_img: (_d = chat.details) === null || _d === void 0 ? void 0 : _d.post_img, post_title: (_e = chat.details) === null || _e === void 0 ? void 0 : _e.post_title, post_country: (_f = chat.details) === null || _f === void 0 ? void 0 : _f.post_country, post_type: "default", sender_id: getDataFromStorage().userInfo.id, recipient_id: getOtherUserId(chat.details), recipient_name: (_g = chat.details) === null || _g === void 0 ? void 0 : _g.rooms[0].name, recipient_avatar: (_h = chat.details) === null || _h === void 0 ? void 0 : _h.rooms[0].avatar, sender_avatar: (_j = getUserInfo()) === null || _j === void 0 ? void 0 : _j.profile_picture, sender_name: (_k = getUserInfo()) === null || _k === void 0 ? void 0 : _k.full_name, status: 0 }, messageContent), extractUtms());
            socket === null || socket === void 0 ? void 0 : socket.emit('send', (_l = chat.details) === null || _l === void 0 ? void 0 : _l.pid, (_m = chat.details) === null || _m === void 0 ? void 0 : _m.oid, (_o = chat.details) === null || _o === void 0 ? void 0 : _o.rooms[0].uid, data, function (mid, sent_at) {
                const messageObject = Object.assign({ sent_at: sent_at, mid: mid, post_shop_avatar: '', post_shop_name: '', room: '', send_date: '', sender_lang: '', sender_platform: '', sender_user_agent: '', timestamp: 0 }, data);
                if (!isConnectionFailed) {
                    setMessages(prevMessages => {
                        return [
                            messageObject,
                            ...prevMessages
                        ];
                    });
                    setTimeout(() => {
                        props.onLoadChats();
                        scrollToBottom();
                    }, 10);
                    resolve(null);
                }
            });
        });
    }, [chat, isBlocked, isConnectionFailed]);
    const retrySendMessageHandler = (message) => {
        if (isConnectionFailed)
            return;
        sendMessageHandler(message);
        setOfflineMessages((prev) => {
            return prev.filter(msg => (msg.mid !== message.mid));
        });
    };
    const removeOfflineMessageHandler = useCallback((message) => {
        setOfflineMessages((prev) => {
            return prev.filter((msg) => {
                return (msg.mid !== message.mid);
            });
        });
    }, []);
    const callEventHandler = useCallback((chat) => {
        var _a, _b, _c, _d;
        if (isPhoneHidden)
            return;
        if (!callRef.current) {
            config.actions.onCallFailed();
            return;
        }
        if (callRef && callRef.current) {
            getUnmaskedMobileNumberApi({ "reveal_key": revealKey, "type": "post" }).then((response => {
                var _a, _b;
                if (response.data.revealed_number) {
                    (_a = callRef === null || callRef === void 0 ? void 0 : callRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute("href", `tel:${response.data.call_phone_number}`);
                    (_b = callRef === null || callRef === void 0 ? void 0 : callRef.current) === null || _b === void 0 ? void 0 : _b.click();
                }
            })).catch((err) => {
                config.actions.onCallFailed();
                console.error(err);
            });
        }
        const utmData = extractUtms();
        loggingCallPostApi({
            params: {
                cName: (_a = utmData === null || utmData === void 0 ? void 0 : utmData.utm) === null || _a === void 0 ? void 0 : _a.campaign,
                cMedium: (_b = utmData === null || utmData === void 0 ? void 0 : utmData.utm) === null || _b === void 0 ? void 0 : _b.medium,
                cSource: (_c = utmData === null || utmData === void 0 ? void 0 : utmData.utm) === null || _c === void 0 ? void 0 : _c.source,
            },
            data: {
                "Logging[post_id]": (_d = chat.details) === null || _d === void 0 ? void 0 : _d.pid
            }
        });
    }, [chat, callRef, isPhoneHidden]);
    useEffect(() => {
        var _a;
        if (isConnectionFailed && chatId !== undefined)
            setChatId(undefined);
        else if (!isConnectionFailed) {
            const utmData = extractUtms();
            const storage = getDataFromStorage();
            getPostViewNodeApi({
                postId: (_a = chat.details) === null || _a === void 0 ? void 0 : _a.pid
            }).then((response) => {
                var _a, _b, _c, _d, _e, _f;
                if (response && response.data) {
                    const lang = config.lang;
                    var payload = response.data;
                    if (payload.archive) {
                        setIsPhoneHidden(true);
                        setRevealKey(null);
                        return;
                    }
                    const postType = (_a = payload.listing.post_type) !== null && _a !== void 0 ? _a : 'default';
                    setChatInfo((old) => {
                        return Object.assign(Object.assign({}, old), { tips: postType === 'JobPoster' ? [...jobTips[lang]] : [...generalTips[lang]] });
                    });
                    setPost({
                        id: (_b = chat.details) === null || _b === void 0 ? void 0 : _b.pid,
                        category: { label: payload.listing.category.reporting_name },
                        subCategory: payload.listing.sub_category.reporting_name,
                        country: storage.country,
                        service: payload.listing.services && payload.listing.services.length > 0 ? payload.listing.services[payload.listing.services.length - 1] : '',
                        city: {
                            name: (payload.listing.city.name_en) ? payload.listing.city.name_en : payload.listing.city.name_english,
                            lat: payload.listing.city ? payload.listing.city.lat : undefined,
                            lng: payload.listing.city ? payload.listing.city.lng : undefined
                        },
                        neighborhoodName: (payload.listing.neighborhood && payload.listing.neighborhood.name_english) ? payload.listing.neighborhood.name_english : '',
                        listingStatus: payload.listing.listing_status,
                        memberId: payload.listing.members_id
                    });
                    if (payload && payload.listing.masked_local_phone && (((_d = (_c = getDataFromStorage().userInfo) === null || _c === void 0 ? void 0 : _c.id) === null || _d === void 0 ? void 0 : _d.toString()) !== ((_f = (_e = payload.listing) === null || _e === void 0 ? void 0 : _e.member_id) === null || _f === void 0 ? void 0 : _f.toString()))) {
                        setIsPhoneHidden(false);
                        setRevealKey(payload.listing.listing_reveal_phone_key);
                    }
                    else {
                        setIsPhoneHidden(true);
                        return;
                    }
                }
            }).catch((error) => {
                console.error(error);
            });
            setChatId(chat.details.rooms[0].uid);
        }
    }, [chat]);
    useEffect(() => {
        if (!socket || !chat)
            return;
        scrollToBottom();
        let timeOut;
        EventEmitter.subscribe("chat-info.load", (data) => {
            const responseTimeInterval = formattingResponseTime(data.avg_response_time);
            setChatInfo({
                responseDuration: translate(responseTimeInterval.key, responseTimeInterval.interval ? [responseTimeInterval.interval] : []),
                hasMore: true,
                tips: []
            });
        });
        EventEmitter.subscribe("is-typing", function () {
            clearTimeout(timeOut);
            setIsTyping(true);
            timeOut = setTimeout(() => {
                setIsTyping(false);
            }, 2000);
        });
        socket === null || socket === void 0 ? void 0 : socket.emit("user-info", chat.details.oid, function (data) {
            EventEmitter.emit("chat-info", data);
        });
        EventEmitter.subscribe("message.inside", (data) => {
            const isOpenedChat = chat.id == data.message.room;
            if (isOpenedChat) {
                setMessages((prevMessages) => {
                    return [
                        data.message,
                        ...prevMessages
                    ];
                });
            }
        });
        socket.on('seen', seenMessageHandler);
        socket.on('delivered', deliveredMessageHandler);
        setIsFetchingDisabled(true);
        loadMessagesHandler({}).then((newMessages) => {
            setMessages([...newMessages]);
            setIsFetchingDisabled(false);
        });
        socket === null || socket === void 0 ? void 0 : socket.emit('is-blocked', getOtherUserId(chat.details), (res) => {
            if (res) {
                setIsBlocked(res);
            }
        });
        return () => {
            socket.off('seen');
            socket.off('delivered');
            EventEmitter.unsubscribe("message.inside");
            EventEmitter.unsubscribe("chat-info.load");
            EventEmitter.unsubscribe("is-typing");
        };
    }, [socket, chat]);
    const ScrollId = isDrawer ? 'kt_drawer_chat_messenger_body' : 'kt_chat_messenger_body';
    const OnlineStatusComponent = ({ className }, message) => {
        const loadComponent = () => {
            if (message.status === MESSAGE_STATUSES.DELIVERED)
                return FaCheckDouble;
            if (message.status === MESSAGE_STATUSES.SEEN) {
                className = `${className ? ` ${className}` : ''} greenCheck`;
                return FaCheckDouble;
            }
            if (message.status === MESSAGE_STATUSES.SENT)
                return FaCheck;
            return FaCheck;
        };
        const Component = loadComponent();
        return React.createElement("span", null,
            React.createElement(Component, { className: className }));
    };
    const OfflineStatusComponent = ({ className }) => {
        const loadComponent = () => {
            return FaTimes;
        };
        const Component = loadComponent();
        return React.createElement("span", null,
            React.createElement(Component, { className: className }));
    };
    const Messages = useCallback(() => {
        return React.createElement(React.Fragment, { key: 'messages' },
            OfflineMessages(),
            OnlineMessages());
    }, [messages, offlineMessages, isConnectionFailed, chat]);
    const OnlineMessages = useCallback(() => {
        return messages.map((message, index) => {
            const humanTime = convertToHuman(message.sent_at);
            const date = translate(humanTime.key, (humanTime === null || humanTime === void 0 ? void 0 : humanTime.interval) ? [humanTime === null || humanTime === void 0 ? void 0 : humanTime.interval] : []);
            const isSender = checkIsSender(message);
            const isSeller = checkIsSeller(message);
            const templateAttr = {};
            const contentClass = `${isDrawer ? '' : 'd-flex '} justify-content-${!isSender ? 'start' : 'end'}`;
            return (React.createElement("div", Object.assign({ key: `message${message.mid}`, className: clsx('d-flex', contentClass, `direction-ltr`, 'ps-3', 'pe-3', { 'd-none': message.template }) }, templateAttr),
                React.createElement("div", { className: clsx(`d-flex pt-6 flex-column direction-trl align-items edit-mesg-chat`, `align-items-${!isSender ? 'start' : 'end'}`) },
                    React.createElement(MessageContent, { message: message, key: `content${message.mid}` }),
                    React.createElement("span", { className: 'text-muted fs-12 mt-2 message-status', key: `Status${message.mid}` },
                        React.createElement("div", null,
                            isSender && React.createElement(OnlineStatusComponent, null),
                            React.createElement("span", { className: "mx-1", key: `date${message.mid}` }, date)),
                        React.createElement(SystemMessageButton, { key: `${message.mid}_systemMessageButton`, message: message, messageType: `${message.type}.${message.subType}`, isSeller: isSeller })))));
        });
    }, [messages]);
    const OfflineMessages = () => {
        return (offlineMessages.map((message, index) => {
            if (message.sender_id !== getDataFromStorage().userInfo.id ||
                message.recipient_id !== getOtherUserId(chat.details) ||
                message.pid !== chat.details.pid)
                return React.createElement(React.Fragment, null);
            const humanTime = convertToHuman(message.sent_at);
            const date = translate(humanTime.key, (humanTime === null || humanTime === void 0 ? void 0 : humanTime.interval) ? [humanTime === null || humanTime === void 0 ? void 0 : humanTime.interval] : []);
            const isSender = checkIsSender(message);
            const templateAttr = {};
            const contentClass = `${isDrawer ? '' : 'd-flex '} justify-content-${!isSender ? 'start' : 'end'} `;
            return (React.createElement("div", Object.assign({ key: `message${message.mid}`, ref: message.mid === messages[messages.length - 1].mid ? messageRef : null, className: clsx('d-flex', contentClass, `direction-ltr`, 'ps-3', 'pe-3') }, templateAttr),
                React.createElement("div", { className: clsx(`d-flex flex-column direction-tlr align-items justify-content-center edit-mesg-chat`, `align-items-${!isSender ? 'start' : 'end'}`) },
                    React.createElement("span", { className: 'text-muted fs-12 message-status dropdown' },
                        React.createElement("button", { type: 'button', className: 'btn btn-sm btn-icon btn-color-primary btn-active-light-primary  d-flex justify-content-center align-items-center', id: "SettingsMenu", "data-bs-toggle": "dropdown", "aria-expanded": "false" },
                            React.createElement(FaEllipsisV, { style: { fontSize: "16px", color: "#000" } })),
                        React.createElement("div", { className: 'dropdown-menu p-0', "aria-labelledby": "SettingsMenu" },
                            React.createElement("ul", { className: "list__dropdown-ul", style: { listStyleType: "none" } },
                                React.createElement("li", { onClick: () => retrySendMessageHandler(offlineMessages[index]), className: `list__dropdown-li ${config.lang === 'ar' ? 'direction-rtl' : 'direction-ltr'} d-flex cursor-pointer grayHoverBg d-flex justify-content-center align-items-center gap-1` },
                                    React.createElement("i", { className: "fa-solid fa-refresh", style: { fontSize: '16px' } }),
                                    React.createElement("div", { className: "list__dropdown-li-div" },
                                        React.createElement("span", { className: "list__dropdown-li-span" }, OFFLINE_MESSAGES[config.lang][0]))),
                                React.createElement("li", { onClick: () => removeOfflineMessageHandler(offlineMessages[index]), className: `list__dropdown-li d-flex ${config.lang === 'ar' ? 'direction-rtl' : 'direction-ltr'} cursor-pointer redHoverBg d-flex justify-content-center align-items-center gap-1` },
                                    React.createElement("i", { className: "fa-solid fa-times", style: { fontSize: '16px' } }),
                                    React.createElement("div", { className: "list__dropdown-li-div" },
                                        React.createElement("span", { className: "list__dropdown-li-span" }, OFFLINE_MESSAGES[config.lang][1]))))))),
                React.createElement("div", { className: clsx(`d-flex pt-6 flex-column direction-trl align-items edit-mesg-chat`, `align-items-${!isSender ? 'start' : 'end'}`) },
                    React.createElement(MessageContent, { message: message, key: `content${message.mid}` }),
                    React.createElement("span", { className: 'text-muted fs-12 mt-2 message-status', key: `Status${message.mid}` },
                        React.createElement(OfflineStatusComponent, null),
                        React.createElement("span", null, ERROR[config.lang])))));
        }));
    };
    const scrollableRootRef = React.useRef(null);
    const lastScrollDistanceToBottomRef = React.useRef();
    // We keep the scroll position when new items are added etc.
    useEffect(() => {
        var _a;
        const scrollableRoot = scrollableRootRef.current;
        const lastScrollDistanceToBottom = (_a = lastScrollDistanceToBottomRef.current) !== null && _a !== void 0 ? _a : 0;
        if (scrollableRoot) {
            scrollableRoot.scrollTop =
                scrollableRoot.scrollHeight - lastScrollDistanceToBottom;
        }
    }, [messages, rootRef]);
    const rootRefSetter = React.useCallback((node) => {
        rootRef(node);
        scrollableRootRef.current = node;
    }, [rootRef]);
    const handleRootScroll = React.useCallback(() => {
        const rootNode = scrollableRootRef.current;
        if (rootNode) {
            const scrollDistanceToBottom = rootNode.scrollHeight - rootNode.scrollTop;
            lastScrollDistanceToBottomRef.current = scrollDistanceToBottom;
        }
    }, []);
    const listingClickHandler = () => {
        if (!post)
            return;
        logViewListingClick(post, EnumElementName.LISTING_SNIPPET);
        setTimeout(() => {
            window.location.href = props.url;
        }, 200);
    };
    return React.createElement(React.Fragment, null,
        React.createElement("div", { className: 'card chat__screen-mobile m-0 p-0 container-fluid d-flex flex-column justify-contrent-between h-100', id: 'kt_chat_messenger' },
            (chatId || !isConnectionFailed) &&
                React.createElement("div", { className: 'card-header chat__header', id: 'kt_chat_messenger_header' },
                    React.createElement("div", { className: "d-flex w-100 justify-content-between" },
                        React.createElement("a", { href: "#", onClick: listingClickHandler, className: 'd-flex align-items-center' },
                            React.createElement("div", { className: "symbol symbol-group symbol-hover symbol-50px chat__symbol-img-ml" },
                                React.createElement("img", { src: chat.details.post_img || 'https://opensooqui2.os-cdn.com/noImage/no-image-ar.png', alt: "", className: "chat__header-img" })),
                            React.createElement("div", { className: "chat__header-div-text w-100" },
                                React.createElement("span", { className: "chat__header-text mb-1 customChatHeaderText" }, chat.details.post_title),
                                React.createElement("div", { className: "fs-6 list__card-subtext customChatHeaderText" }, chat.details.rooms[0].name))),
                        React.createElement("a", { href: "#", style: { display: "none" }, ref: callRef }),
                        React.createElement("div", { className: 'd-flex gap-1 ' },
                            (!isPhoneHidden) && React.createElement("div", { className: "text-end p-0 chat__header-box-time" },
                                React.createElement("button", { className: "btn btn-sm btn-icon btn-active-light-primary me-1", type: "button", "data-bs-toggle": "tooltip", onClick: () => callEventHandler(chat) },
                                    React.createElement("i", { className: "fa-solid fs-1 fa-phone" }))),
                            React.createElement("div", { className: "p-0 chat__header-box-time" },
                                React.createElement(ChatSettings, { chat: chat, onLoadChats: props.onLoadChats, clearCurrentChatHandler: props.clearCurrentChatHandler }))))),
            isDeviceMobile() && isConnectionFailed && React.createElement(NotConnectedInternet, null),
            (chatId || !isConnectionFailed) && React.createElement("div", { className: "flex-grow-1", style: { overflow: 'scroll' }, ref: rootRefSetter, onScroll: handleRootScroll },
                React.createElement("div", { id: ScrollId, ref: rootRef, className: "mt-0 mb-0 pb-5 pt-5", style: {
                        width: "100%",
                        display: "flex",
                        overflowY: 'scroll',
                        flexDirection: "column-reverse",
                        overflowX: 'hidden'
                    }, key: ScrollId, "data-kt-element": 'messages', "data-kt-scroll": 'true', "data-kt-scroll-activate": '{default: false, lg: true}', "data-kt-scroll-max-height": 'auto', "data-kt-scroll-dependencies": isDrawer ? '#kt_drawer_chat_messenger_header, #kt_drawer_chat_messenger_footer' : '#kt_header, #kt_app_header, #kt_app_toolbar, #kt_toolbar, #kt_footer, #kt_app_footer, #kt_chat_messenger_header, #kt_chat_messenger_footer', "data-kt-scroll-wrappers": isDrawer ? '#kt_drawer_chat_messenger_body' : '#kt_content, #kt_app_content, #kt_chat_messenger_body', "data-kt-scroll-offset": isDrawer ? '0px' : '5px' },
                    (chatId) && isTyping && React.createElement("div", null, translate('typing')),
                    Messages(),
                    !chatInfo.hasMore &&
                        React.createElement("div", { style: { width: '100%' }, key: "Fixed_tips_duration" },
                            chatInfo && chatInfo.tips && chatInfo.tips.length > 0 ? React.createElement(GeneralTips, { tips: chatInfo.tips }) : React.createElement(React.Fragment, null),
                            React.createElement(Spearator, null),
                            (chatInfo === null || chatInfo === void 0 ? void 0 : chatInfo.responseDuration) && !chatInfo.hasMore && React.createElement(ResponseTime, { responseDuration: chatInfo === null || chatInfo === void 0 ? void 0 : chatInfo.responseDuration }),
                            React.createElement(Spearator, null)),
                    chatInfo.hasMore && !isFetchingDisabled && React.createElement("div", { ref: infiniteScrollRef }))),
            React.createElement("div", { className: "m-0 p-0" }, (!isConnectionFailed || chatId) && React.createElement(SendMessageForm, { sendMessageHandler: sendMessageHandler, saveOfflineMessageHandler: saveOfflineMessageHandler, chat: chat, isBlocked: isBlocked })),
            (!chatId && isConnectionFailed) && React.createElement(NotConnected, null)));
};
export default Chat;
